import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Crown, Mail, Trash2, UserMinus, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { errMsg, fmtDay, useApi } from "@/lib/api";
import TeamActivity from "@/components/TeamActivity";

const useFirm = () => {
  const api = useApi();
  return useQuery({ queryKey: ["firm"], queryFn: async () => (await api.get("/firm")).data });
};

const InviteForm = ({ onDone, full }) => {
  const api = useApi();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/firm/invites", { email });
      toast.success(data.added ? `${data.email} was added to your team` : `Invite saved. ${data.email} joins when they sign in with that email.`);
      setEmail("");
      onDone();
    } catch (err) {
      toast.error(errMsg(err, "Couldn't invite"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2" data-testid="invite-form">
      <input data-testid="invite-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="colleague@yourfirm.com" className="h-11 flex-1 px-3 rounded-md border border-slate-300 bg-white text-sm" disabled={full} />
      <button data-testid="invite-submit-btn" disabled={busy || full} className="h-11 px-5 rounded-md bg-[#1E293B] text-white text-sm font-semibold hover:bg-blue-600 transition-colors disabled:opacity-60 flex items-center gap-2 justify-center">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />} Invite
      </button>
    </form>
  );
};

const SeatEditor = ({ firm, onDone }) => {
  const api = useApi();
  const [seats, setSeats] = useState(firm.seats);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/firm/seats", { seats });
      toast.success(`Seats updated to ${seats}. Stripe will prorate the change.`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e, "Couldn't change seats"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-2" data-testid="seat-editor">
      <input data-testid="team-seats-input" type="number" min={2} max={100} value={seats} onChange={(e) => setSeats(parseInt(e.target.value, 10) || 2)} className="w-20 h-9 px-2 rounded-md border border-slate-300 text-center tabular-nums" />
      <button data-testid="team-seats-save-btn" disabled={busy || seats === firm.seats} onClick={save} className="h-9 px-3 rounded-md border border-slate-300 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50">Update seats</button>
    </div>
  );
};

const NameEditor = ({ firm, onDone }) => {
  const api = useApi();
  const [name, setName] = useState(firm.name);
  const [editing, setEditing] = useState(false);
  if (!editing) return (
    <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-3" data-testid="team-name">
      {firm.name}
      {firm.is_owner && <button data-testid="team-rename-btn" onClick={() => setEditing(true)} className="text-slate-400 hover:text-slate-700" aria-label="Rename"><Pencil size={16} /></button>}
    </h1>
  );
  const save = async () => {
    try { await api.patch("/firm", { name }); setEditing(false); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex gap-2">
      <input data-testid="team-name-input" value={name} onChange={(e) => setName(e.target.value)} className="h-10 px-3 rounded-md border border-slate-300 text-lg font-semibold" maxLength={80} />
      <button data-testid="team-name-save-btn" onClick={save} className="h-10 px-4 rounded-md bg-[#1E293B] text-white text-sm font-semibold">Save</button>
    </div>
  );
};

export default function TeamPage() {
  const api = useApi();
  const qc = useQueryClient();
  const { data, isLoading } = useFirm();
  const refresh = () => { qc.invalidateQueries({ queryKey: ["firm"] }); qc.invalidateQueries({ queryKey: ["me"] }); };
  const act = async (fn, ok) => {
    try { await fn(); toast.success(ok); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };

  if (isLoading) return <main className="max-w-4xl mx-auto px-4 py-16"><div className="h-40 bg-white rounded-lg animate-pulse" /></main>;
  if (!data?.firm) return (
    <main className="max-w-xl mx-auto px-4 py-20 text-center" data-testid="team-empty">
      <Building2 className="mx-auto text-slate-400" size={36} />
      <h1 className="font-heading text-2xl font-bold mt-4">You're not on a team yet</h1>
      <p className="text-slate-600 mt-2">Get a Team or Firm plan to give everyone at your firm unlimited exports. If a colleague invited you, sign in with the email they used.</p>
      <Link to="/pricing#firms" data-testid="team-see-plans-btn" className="mt-6 inline-flex h-11 px-5 rounded-md bg-[#1E293B] text-white text-sm font-semibold items-center hover:bg-blue-600">See team plans</Link>
    </main>
  );

  const { firm, members, invites } = data;
  const full = firm.seats_used >= firm.seats;
  return (
    <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-8" data-testid="team-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-amber-600 font-semibold">Your team</p>
          <div className="mt-2"><NameEditor firm={firm} onDone={refresh} /></div>
          <p className="text-sm text-slate-600 mt-1" data-testid="team-summary">
            <span className="font-semibold tabular-nums">{firm.seats_used} of {firm.seats}</span> seats used - {firm.active ? <span className="text-green-700 font-semibold">Active</span> : <span className="text-red-600 font-semibold">Subscription inactive</span>} - owner {firm.owner_email}
          </p>
        </div>
        {firm.is_owner && firm.per_seat && <SeatEditor firm={firm} onDone={refresh} />}
      </div>

      {firm.is_owner && (
        <section className="bg-white border border-slate-200 rounded-lg p-6 space-y-3">
          <h2 className="font-heading font-bold text-lg">Invite a teammate</h2>
          <p className="text-sm text-slate-600">They join automatically the next time they sign in to DocketZip with this email. Let them know to sign up.</p>
          <InviteForm onDone={refresh} full={full} />
          {full && <p className="text-sm text-amber-700" data-testid="team-full-note">All seats are in use. {firm.per_seat ? "Add seats above" : "Remove someone"} to invite more.</p>}
        </section>
      )}

      <section className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="team-members">
        {members.map((m) => (
          <div key={m.user_id} className="flex items-center justify-between gap-3 px-5 py-4" data-testid={`team-member-${m.user_id}`}>
            <div className="min-w-0">
              <div className="font-semibold truncate flex items-center gap-2">{m.email}{m.is_owner && <Crown size={14} className="text-amber-500" />}</div>
              {m.name && <div className="text-xs text-slate-500">{m.name}</div>}
            </div>
            {firm.is_owner && !m.is_owner && (
              <button data-testid={`team-remove-${m.user_id}`} onClick={() => window.confirm(`Remove ${m.email} from the team?`) && act(() => api.delete(`/firm/members/${m.user_id}`), "Member removed")} className="h-9 px-3 rounded-md text-sm text-slate-600 hover:bg-red-50 hover:text-red-600 flex items-center gap-1.5"><UserMinus size={14} /> Remove</button>
            )}
          </div>
        ))}
        {invites.map((i) => (
          <div key={i.email} className="flex items-center justify-between gap-3 px-5 py-4 bg-slate-50/60" data-testid={`team-invite-${i.email}`}>
            <div className="min-w-0">
              <div className="font-medium truncate text-slate-700">{i.email}</div>
              <div className="text-xs text-slate-500">Invited {fmtDay(i.created_at)} - waiting for them to sign in</div>
            </div>
            <button data-testid={`team-cancel-invite-${i.email}`} onClick={() => act(() => api.delete(`/firm/invites/${encodeURIComponent(i.email)}`), "Invite cancelled")} className="h-9 px-3 rounded-md text-sm text-slate-600 hover:bg-red-50 hover:text-red-600 flex items-center gap-1.5"><Trash2 size={14} /> Cancel</button>
          </div>
        ))}
      </section>

      {firm.is_owner && <TeamActivity />}

      {!firm.is_owner && (
        <button data-testid="team-leave-btn" onClick={() => window.confirm("Leave this team? You'll go back to the Free plan.") && act(() => api.post("/firm/leave"), "You left the team")} className="h-10 px-4 rounded-md border border-slate-300 text-sm font-semibold hover:bg-red-50 hover:text-red-600">Leave team</button>
      )}
    </main>
  );
}
